import React from 'react';
import { useQueryClient } from 'react-query';
import { Box, Grid, LinearProgress, Typography } from '@mui/material';
import sparkLoadingAnimation from 'animations/sparkLoading.json';
import { WorkspaceContext } from 'contexts/workspaceContext';
import { useWorkspaceByID } from 'hooks/Workspace/useWorkspaceByID';
import type { WorkspaceContextValue } from 'contexts/workspaceContext';
import type { Workspace } from 'types/Workspace';
import { useCurrentUser } from 'hooks/common/useCurrentUser';
import { useLocation, useNavigate } from 'react-router-dom';
import Lottie from 'react-lottie';
import { useUserStore, useWorkspaceStore } from 'stores';
import { SPARK_RUN_ROUTE_REGEX } from 'constants/regex';

// Define the types
interface WorkspaceContextProps {
  children: React.ReactNode;
}

// Create a Provider component
const WorkSpaceContextProvider = ({ children }: WorkspaceContextProps) => {
  const setStoreWorkspace = useWorkspaceStore(
    React.useCallback((state) => state.setWorkspace, [])
  );

  const currentUserID = useUserStore(
    React.useCallback((state) => state.currentUserID, [])
  );

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const { applicationUser } = useCurrentUser();
  const [currentWorkspaceID, setCurrentWorkspaceID] = React.useState<
    string | null
  >(null);

  const defaultWorkspaceID = applicationUser?.attributes.defaultWorkspaceID;
  React.useEffect(() => {
    const isSparkRunRoute = SPARK_RUN_ROUTE_REGEX.test(pathname);
    // TODO: Handle the case when the user is not logged in
    if (!defaultWorkspaceID && !!currentUserID && !isSparkRunRoute) {
      navigate('/onboarding');
      return;
    }

    if (!currentWorkspaceID) {
      setCurrentWorkspaceID(defaultWorkspaceID || '');
    }
  }, [
    currentUserID,
    currentWorkspaceID,
    defaultWorkspaceID,
    navigate,
    pathname,
  ]);

  const { workspace, isLoading, isFetching } =
    useWorkspaceByID(defaultWorkspaceID);

  const isLoadingOrFetching = isLoading || isFetching;

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: sparkLoadingAnimation,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };
  if (isLoadingOrFetching) {
    return (
      <Grid
        height='100vh'
        container
        justifyContent='center'
        alignItems='center'
      >
        <Box display='flex' alignItems='center' flexDirection='column' gap={1}>
          <Lottie options={defaultOptions} height={100} width={100} />
          <Typography
            color={(theme) => theme.palette.grey[800]}
            variant='h6'
            fontFamily='Mont-semibold'
          >
            Loading Workspace...
          </Typography>
          <Box sx={{ width: '100%' }}>
            <LinearProgress />
          </Box>
        </Box>
      </Grid>
    );
  }
  if (workspace) {
    // update workspace in store
    setStoreWorkspace(workspace);
  }

  // Function to set the current workspace
  const setWorkspace = (workspaceID: string) => {
    queryClient.clear();
    setCurrentWorkspaceID(workspaceID);
  };

  // Function to get the current workspace
  const getWorkspace = (): Workspace | undefined => {
    if (!workspace) {
      return;
    }
    return workspace;
  };
  // Provide the context value to the children
  const contextValue: WorkspaceContextValue = {
    currentWorkspaceID,
    setWorkspace,
    getWorkspace,
  };

  return (
    <WorkspaceContext.Provider value={contextValue}>
      {children}
    </WorkspaceContext.Provider>
  );
};

export default WorkSpaceContextProvider;
